import Quill from 'quill';
import React, { forwardRef, useEffect, useLayoutEffect, useRef } from 'react';

const Link = Quill.import('formats/link');

Link.sanitize = function (url) {
    if (!/^https?:\/\//i.test(url)) {
        return "https://" + url;
    }

    return url;
};

Quill.register(Link, true);

const RichTextEditor = forwardRef(({ readOnly, defaultValue, onTextChange, onSelectionChange, minHeight = '150px' }, ref) => {
    const containerRef = useRef(null);
    const defaultValueRef = useRef(defaultValue);
    const onTextChangeRef = useRef(onTextChange);
    const onSelectionChangeRef = useRef(onSelectionChange);

    useLayoutEffect(() => {
        onTextChangeRef.current = onTextChange;
        onSelectionChangeRef.current = onSelectionChange;
    });

    useEffect(() => {
        ref.current?.enable(!readOnly);
    }, [ref, readOnly]);

    useEffect(() => {
        const container = containerRef.current;
        const editorContainer = container.appendChild(
            container.ownerDocument.createElement('div'),
        );
        const quill = new Quill(editorContainer, {
            theme: 'snow',
        });

        quill.root.style.minHeight = minHeight;

        ref.current = quill;

        if (defaultValueRef.current) {
            quill.clipboard.dangerouslyPasteHTML(defaultValueRef.current);
        }

        quill.on(Quill.events.TEXT_CHANGE, (...args) => {
            const html = quill.root.innerHTML;
            onTextChangeRef.current?.(html);
        });

        quill.on(Quill.events.SELECTION_CHANGE, (...args) => {
            onSelectionChangeRef.current?.(...args);
        });

        return () => {
            ref.current = null;
            container.innerHTML = '';
        };
    }, [ref]);

    return <div ref={containerRef} style={{ minHeight: 150 }}></div>;
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;